import { useEffect, useState } from "react";
import { supAPI } from "./supApi";
import { toast } from "sonner";
import { Plus, X, Save } from "lucide-react";
import { EmptyState } from "./SupplyItems";

export default function SupplyVendors() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const load = () => supAPI.vendors().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="sup-vendors">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-[16px] font-semibold text-hocp-navy">Vendors</h2>
          <p className="text-[12.5px] text-slate-500">Supplier master — used on purchase orders.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="sup-vendor-new"
                className="h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2">
          <Plus className="w-4 h-4"/> Add Vendor
        </button>
      </div>

      {!rows.length ? <EmptyState msg="No vendors yet. Add your first supplier."/> : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11.5px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">GST</th>
                <th className="text-left px-4 py-3">Payment Terms</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(v => (
                <tr key={v.id} data-testid={`sup-vendor-row-${v.id}`} className="hover:bg-slate-50/50">
                  <td className="px-4 py-3 mono text-slate-500">{v.code}</td>
                  <td className="px-4 py-3 font-medium text-hocp-navy">{v.name}</td>
                  <td className="px-4 py-3 text-slate-600">
                    <div>{v.contact_person || <span className="text-slate-300">—</span>}</div>
                    <div className="text-[11.5px] text-slate-400">{v.phone || v.email || ""}</div>
                  </td>
                  <td className="px-4 py-3 mono text-slate-500">{v.gst_number || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{v.payment_terms || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <VendorDialog onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }}/>
      )}
    </div>
  );
}

function VendorDialog({ onClose, onCreated }) {
  const [f, setF] = useState({ code: "", name: "", contact_person: "", phone: "", email: "",
                                 address: "", gst_number: "", payment_terms: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.code || !f.name) { toast.error("Code and name required"); return; }
    setSaving(true);
    try { await supAPI.createVendor(f); toast.success("Vendor created"); onCreated(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">New Vendor</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4 text-slate-500"/></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <F label="Code"><input value={f.code} onChange={e=>setF({...f,code:e.target.value.toUpperCase()})} data-testid="new-vendor-code" className={cls}/></F>
          <F label="Name"><input value={f.name} onChange={e=>setF({...f,name:e.target.value})} data-testid="new-vendor-name" className={cls}/></F>
          <F label="Contact person"><input value={f.contact_person} onChange={e=>setF({...f,contact_person:e.target.value})} className={cls}/></F>
          <F label="Phone"><input value={f.phone} onChange={e=>setF({...f,phone:e.target.value})} className={cls}/></F>
          <F label="Email"><input value={f.email} onChange={e=>setF({...f,email:e.target.value})} className={cls}/></F>
          <F label="GST number"><input value={f.gst_number} onChange={e=>setF({...f,gst_number:e.target.value})} className={cls + " mono"}/></F>
          <F label="Payment terms" full><input value={f.payment_terms} placeholder="e.g. Net 30" onChange={e=>setF({...f,payment_terms:e.target.value})} className={cls}/></F>
          <F label="Address" full><textarea rows={2} value={f.address} onChange={e=>setF({...f,address:e.target.value})} className={cls + " py-2"}/></F>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="h-9 px-3.5 rounded-lg text-slate-600 text-[13px] hover:bg-slate-100">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="new-vendor-submit"
                  className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2">
            <Save className="w-4 h-4"/> {saving ? "Saving…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

const cls = "w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px] outline-none focus:border-hocp-navy transition";
function F({ label, children, full }) {
  return <label className={`block ${full ? "col-span-2" : ""}`}><div className="text-[11.5px] text-slate-500 mb-1 font-medium">{label}</div>{children}</label>;
}
